import React, { useRef } from 'react';
import styled from 'styled-components';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import {
  ConciergeRing,
  conciergeBadgeCss,
  conciergeLabelCss,
  conciergePillCss,
  conciergeSurfaceCss,
} from 'src/concierge/concierge-card-chrome';
import { ConciergeVariables } from 'src/concierge/concierge-variables';
import { useConciergeThemeScope } from 'src/concierge/use-concierge-theme-scope';
import type { ProductListConciergeEntry } from './product-list-concierge-content';

/**
 * Themed from the store's Concierge theme (partner, Admin, then stock), scoped
 * to this card: `concierge-mode` and `data-concierge-theme` re-skin only the
 * card's own `--dcm-*` tokens.
 */
export function ProductListConciergeCard({ content, onClick }: ProductListConciergeEntry): JSX.Element {
  const { overridesCss, scopeProps } = useConciergeThemeScope();

  const shellRef = useRef<HTMLElement>(null);
  return (
    <>
      <ConciergeVariables $tenantOverrides={overridesCss} />
      <Card ref={shellRef} aria-label='AI Concierge' data-testid='product-list-concierge-card' {...scopeProps}>
        <ConciergeRing targetRef={shellRef} testId='product-list-concierge-card-ring' />
        <GlowClip aria-hidden='true' />
        <Content>
          <Sparkle aria-hidden='true'>
            <BlackSparkleIcon width={24} height={24} fill='currentColor' />
          </Sparkle>
          <Label>{content.sectionLabel}</Label>
          <Headline title={content.headline}>{content.headline}</Headline>
          <Subtext>{content.subtext}</Subtext>
          <Cta type='button' onClick={onClick}>
            {content.button}
          </Cta>
        </Content>
      </Card>
    </>
  );
}

const Card = styled.section`
  ${conciergeSurfaceCss}
  border-radius: var(--dcm-radius-cards, 8px);
  grid-column: 1 / -1;
  isolation: isolate;
  padding: 22px 18px;
  position: relative;
  width: 100%;
`;

/* Clips the sparkle glow to the card without clipping the ring's own glow. */
const GlowClip = styled.div`
  border-radius: inherit;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;

  /* Centered on the sparkle: card top padding (22px) + half the icon (20px). */
  &::before {
    background: radial-gradient(
      closest-side,
      color-mix(in oklch, var(--dcm-concierge-accent, #d4a843) 22%, transparent),
      transparent 70%
    );
    content: '';
    height: 220px;
    left: 50%;
    position: absolute;
    top: 42px;
    transform: translate(-50%, -50%);
    width: 220px;
  }
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 auto;
  max-width: 640px;
  min-width: 0;
  position: relative;
  text-align: center;
`;

const Sparkle = styled.span`
  ${conciergeBadgeCss}
  border-radius: var(--dcm-product-list-concierge-card-icon-border-radius, 50%);
`;

const Label = styled.p`
  ${conciergeLabelCss}
`;

const Headline = styled.h2`
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--dcm-font-size-lg, 16px);
  font-weight: var(--dcm-font-weight-bold, 700);
  line-height: 1.25;
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: var(--dcm-font-size-xl, 18px);
  }
`;

const Subtext = styled.p`
  color: var(--dcm-color-text-secondary, #889591);
  font-size: var(--dcm-font-size-sm, 13px);
  font-weight: var(--dcm-font-weight-normal, 400);
  margin: 0;
`;

const Cta = styled.button`
  ${conciergePillCss}
`;
